import { useEffect, useMemo, useState } from "react";

import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import type SelectionDropdown from "../models/selection-dropdown/SelectionDropdown";
import ManualSelectionItem from "../models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../models/selection-dropdown-item/types";

type Deps<Item extends SelectionDropdownItem> = {
    limit?: number;
    numberOfResults: number;
    numberOfSelectedItems: number;
    mapLimitedListOfItems?: ((listOfItems: readonly number[]) => readonly Item[]) | undefined;
    onSelectionChange(item: SelectionDropdownItem): void;
};

type HookValue<Item extends SelectionDropdownItem> = {
    activeSelectionItem: Item | SelectionDropdownItem;
    dropdownItems: readonly Item[];
    isSelectionEmpty: boolean;
    manualSelectionItem: ManualSelectionItem;
    setActiveSelectionItem: (item: Item) => void;
};

function useActionsPanelState<Item extends SelectionDropdownItem = SelectionDropdownItem>(
    selectionDropdown: Readonly<SelectionDropdown>,
    deps: Deps<Item>,
): HookValue<Item> {
    const { limit, numberOfResults, numberOfSelectedItems, onSelectionChange, mapLimitedListOfItems } = deps;

    const manualSelectionItem = new ManualSelectionItem(
        numberOfSelectedItems,
        undefined,
        selectionDropdown.i18nKeyModifier,
    );

    const dropdownItems = useMemo(() => {
        return selectionDropdown.getItems(numberOfResults, limit, mapLimitedListOfItems);
    }, [selectionDropdown, numberOfResults, limit, mapLimitedListOfItems]);

    const [activeSelectionItem, setActiveSelectionItem] = useState<Item | SelectionDropdownItem>(
        selectionDropdown.getInitiallySelectedItem(dropdownItems),
    );

    const isSelectionEmpty = activeSelectionItem.id === ManualSelectionItem.ID && numberOfSelectedItems === 0;

    useEffect(() => {
        if (!salesSettingsHelper.isDataCreditsEnabled) {
            const expectedSelectedItem = selectionDropdown.getInitiallySelectedItem(dropdownItems);

            setActiveSelectionItem(expectedSelectedItem);
        }
    }, [dropdownItems]);

    useEffect(() => {
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            setActiveSelectionItem(manualSelectionItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberOfSelectedItems]);

    useEffect(() => {
        onSelectionChange(activeSelectionItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSelectionItem.id]);

    return {
        activeSelectionItem,
        dropdownItems,
        isSelectionEmpty,
        manualSelectionItem,
        setActiveSelectionItem,
    };
}

export default useActionsPanelState;
